import {
  CHART_DATASETS,
  SEP_PLAN_TAB_LABELS,
  SUPPLIER_STATUS,
  SUPPLIER_STATUS_CONTROL,
  SUPPLIER_STATUS_DONOTUSE,
  SUPPLIER_STATUS_GROW,
  SUPPLIER_STATUS_LIMITED_USE,
  SUPPLIER_STATUS_MAINTAIN,
  SUPPLIER_STATUS_REDUCE
} from '../shared/AppConstants';
import { gridClasses } from '@mui/x-data-grid';
import type { Theme } from '@mui/material/styles';

type SpendChartColumn = {
  denominator: number;
  numerator: number;
  percentage: number;
  spendType: string;
  values: {
    grow: number;
    maintain: number;
    control: number;
    reduce: number;
    donotuse: number;
    limiteduse: number;
  };
};

export type SpendTableRow = {
  id: string;
  rowName: string;
  [spendType: string]: string | number; // Dynamic keys for spend types
};

export const transformSpendDataForTable = (
  chartColumnsData: SpendChartColumn[]
): SpendTableRow[] => {
  // Create the 8 rows for the table with the preset fields
  const rows: SpendTableRow[] = [
    {
      id: SEP_PLAN_TAB_LABELS.TOTAL_ROW_ID,
      rowName: SEP_PLAN_TAB_LABELS.TOTAL_SPEND
    },
    {
      id: SEP_PLAN_TAB_LABELS.SUPPLIER_STATUS_ROW_ID,
      rowName: SUPPLIER_STATUS
    },
    { id: CHART_DATASETS.GROW_TOTAL, rowName: SUPPLIER_STATUS_GROW },
    { id: CHART_DATASETS.MAINTAIN_TOTAL, rowName: SUPPLIER_STATUS_MAINTAIN },
    { id: CHART_DATASETS.CONTROL_TOTAL, rowName: SUPPLIER_STATUS_CONTROL },
    {
      id: CHART_DATASETS.LIMITED_USE_TOTAL,
      rowName: SUPPLIER_STATUS_LIMITED_USE
    },
    { id: CHART_DATASETS.REDUCE_TOTAL, rowName: SUPPLIER_STATUS_REDUCE },
    { id: CHART_DATASETS.DONOTUSE_TOTAL, rowName: SUPPLIER_STATUS_DONOTUSE }
  ];

  const calculatePercentage = (spend: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((spend / total) * 100);
  };

  // Take the spend data from each column and fill in the rows
  chartColumnsData.forEach((columnData) => {
    const totalSpend =
      columnData.values.grow +
      columnData.values.maintain +
      columnData.values.control +
      columnData.values.reduce +
      columnData.values.donotuse +
      columnData.values.limiteduse;

    rows[0][columnData.spendType] = totalSpend;
    rows[2][columnData.spendType] = columnData.values.grow;
    rows[2][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.grow,
      totalSpend
    );
    rows[3][columnData.spendType] = columnData.values.maintain;
    rows[3][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.maintain,
      totalSpend
    );
    rows[4][columnData.spendType] = columnData.values.control;
    rows[4][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.control,
      totalSpend
    );
    rows[5][columnData.spendType] = columnData.values.limiteduse;
    rows[5][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.limiteduse,
      totalSpend
    );
    rows[6][columnData.spendType] = columnData.values.reduce;
    rows[6][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.reduce,
      totalSpend
    );
    rows[7][columnData.spendType] = columnData.values.donotuse;
    rows[7][`${columnData.spendType}_percent`] = calculatePercentage(
      columnData.values.donotuse,
      totalSpend
    );
  });

  return rows;
};

export const sepSpendGridStyle = (theme: Theme) => ({
  [`& .${gridClasses.cell}`]: {
    py: 4,
    flexWrap: 'wrap',
    alignContent: 'center'
  },
  [`& .${gridClasses.columnHeader}`]: {
    backgroundColor: theme.fuel6Tokens.JddcColorBackgroundPrimary,
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    borderRadius: 0
  },
  [`& .${gridClasses.row}`]: {
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    borderRadius: 0
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    borderRadius: 0
  }
});

export const supplierSpendShiftGridStyle = (theme: Theme) => ({
  '--DataGrid-rowBorderColor': theme.fuel6Tokens.JddcColorBorderSubtle,
  [`& .${gridClasses.cell}`]: {
    py: 1,
    px: 1,
    flexWrap: 'nowrap',
    alignContent: 'center',
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    fontSize: '0.75rem',
    overflow: 'hidden'
  },
  [`& .${gridClasses.cell} .MuiTypography-root`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%'
  },
  [`& .${gridClasses.columnHeader}`]: {
    backgroundColor: theme.fuel6Tokens.JddcColorBackgroundPrimary,
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    borderRadius: 0,
    px: 1
  },
  [`& .${gridClasses.columnHeaderTitle}`]: {
    fontSize: '0.75rem'
  },
  [`& .${gridClasses.columnSeparator}`]: {
    display: 'none'
  },
  [`& .${gridClasses.row}`]: {
    borderBottom: 'none',
    borderRadius: 0
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderBottom: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
    borderRadius: 0
  },
  [`& .MuiDataGrid-filler`]: {
    borderTop: 'none'
  },
  [`& .MuiDataGrid-columnHeader--fillerEmpty`]: {
    borderBottom: 'none'
  },
  [`& .MuiDataGrid-columnHeader .MuiBadge-badge`]: {
    display: 'none'
  },
  [`& .name-cell`]: {
    flexWrap: 'nowrap',
    display: 'flex',
    overflow: 'hidden'
  },
  [`& .name-cell .MuiTypography-root`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    maxWidth: '100%'
  },
  [`& .name-cell a`]: {
    overflow: 'hidden',
    display: 'block',
    maxWidth: '100%'
  },
  [`& .row-section-header`]: {
    backgroundColor: theme.fuel6Tokens.JddcColorBackgroundSecondary
  }
});
